// Pictures of each grape's typical flavours, shown in the Tip on the "What is
// the grape variety?" question. The pictures were drawn from the three flavour
// words in questions_v2.metadata[option].description, so those words are shown
// beside them.
//
// import.meta.glob with ?url gives URLs only: a file is fetched when an <img>
// renders it, not when this module loads.
const FILES = import.meta.glob('../assets/grape-tips/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

// Answer-card pictures: the grape's vine leaf in front of one shared white or
// red glass. Same lazy ?url globbing as above.
const CARD_FILES = import.meta.glob('../assets/grape-cards/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

const normalize = (name) =>
  String(name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Asset slug by file name, e.g. 'sauvignon-blanc'.
const URL_BY_SLUG = Object.fromEntries(
  Object.entries(FILES).map(([path, url]) => [path.split('/').pop().replace(/\.webp$/, ''), url]),
);

// A name's slug is the name itself with spaces as dashes, except the DB's
// "Shiraz / Syrah", which also arrives as either word alone.
const ALIASES = {
  'shiraz / syrah': 'shiraz',
  'shiraz/syrah': 'shiraz',
  syrah: 'shiraz',
};

const CARD_URL_BY_SLUG = Object.fromEntries(
  Object.entries(CARD_FILES).map(([path, url]) => [path.split('/').pop().replace(/\.webp$/, ''), url]),
);

const slugFor = (name) => {
  const key = normalize(name);
  if (!key) return null;
  return ALIASES[key] || key.replace(/ /g, '-');
};

export function grapeTipImage(name) {
  const slug = slugFor(name);
  return (slug && URL_BY_SLUG[slug]) || null;
}

// The picture for a grape's answer card, or null when the whole name is not a
// known grape (exact match only, so flavour lists and other options are safe).
export function grapeCardImage(name) {
  const slug = slugFor(name);
  return (slug && CARD_URL_BY_SLUG[slug]) || null;
}

/**
 * @param {string[]} options  the options on screen (after any 50/50 removal)
 * @param {Record<string, {description?: string}>} metadata  currentQuestion.metadata
 * @returns {{label: string, image: string, words: string|undefined}[]}
 *   one entry per option that has a picture, in the order given.
 */
export function grapeTipsFor(options, metadata) {
  if (!Array.isArray(options)) return [];
  return options.flatMap((label) => {
    const image = grapeTipImage(label);
    if (!image) return [];
    return [{ label, image, words: metadata?.[label]?.description }];
  });
}
